import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { buildBrandedCloudinaryImageTransforms } from '@/layout/Seo/SeoHelper'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getOffersForProject, Offer } from '@/services/PifService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PayItForwardExpressCheckoutView } from '@/views/PayItForward/PayItForwardExpressCheckoutView'
import { TheatricalPayItForwardView } from '@/views/PayItForward/TheatricalPayItForwardView'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  locale: string
  offers: Offer[]
  projectMeta: ProjectMeta
  newDavidPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
}

// Hardcoded theatrical release data for David movie
const davidTheatricalRelease: TheatricalReleaseResult = {
  id: 'david-theatrical',
  theatricalSlug: slugs.david,
  title: 'DAVID',
  catalogContentId: null,
  groupProductVariantId: null,
  hypebotCode: null,
  verticalPoster: 'https://images.angelstudios.com/image/upload/v1760658989/david/frame_5_play.webp',
  horizontalPoster: 'https://images.angelstudios.com/image/upload/v1760658989/david/frame_5_play.webp',
  projectSlug: slugs.david,
  soldOutShowtimeCount: 0,
  yesterdaySoldTicketCount: 0,
  backdropImage: null,
  backdropMobileImage: null,
  logoImage: null,
  logoImageWeb: null,
  critics: null,
  theatricalReleaseRegions: [],
  movieDuration: null,
  externalCta: null,
  region: {
    id: 'david-region-us',
    trailerUrl:
      'https://content.angelstudios.com/david/David_tCTA_NealDavidAnnouncement_l-en_wNOCAP-SDH_16x9_f23_y20251008_iJSVS_1-lowres.mp4',
    seoImage: null,
    releaseDate: '2025-12-19',
    redirectTicketsPage: null,
    ticketsEmergencyRedirectUrl: null,
    freeTicketRequestAmount: null,
    isGroupTicketsEnabled: false,
    ticketsStatus: null,
    trailerThumbnail: 'https://images.angelstudios.com/image/upload/v1760658989/david/frame_5_play.webp',
    regionName: 'United States',
    countryCode: 'US',
    useSimpleWaitlistOverlay: false,
    defaultTicketingProviderSlug: null,
    partnerUrl: null,
    faqs: [
      {
        question: 'What is the David Movie?',
        answer:
          "David is the epic story of one of history's greatest legends. Experience the action, drama and adventure of the shepherd who became a king.",
      },
      {
        question: 'How did you choose the goal of 1 million tickets?',
        answer:
          'We believe the story of David can reach millions. With your support through Pay it Forward, we can ensure that everyone who wants to see this film has the opportunity - regardless of their financial situation.',
      },
      {
        question: 'When do tickets become available?',
        answer:
          'Tickets for David are available for pre-order now! The film releases in theaters on December 19, 2025. Pre-order your tickets today to secure your seats.',
      },
      {
        question: 'Why should I Pay it Forward to David?',
        answer:
          "When you Pay it Forward to David, you're providing free tickets to those who might not otherwise be able to afford to see this powerful story. Your generosity helps spread this inspiring message to every corner of the world.",
      },
      {
        question: 'How will this money be used?',
        answer:
          'Your Pay it Forward contribution goes directly toward purchasing free tickets for those who claim them. This ensures that financial barriers never prevent anyone from experiencing this epic story.',
      },
      {
        question: 'What happens if all tickets are not claimed?',
        answer:
          'If some tickets remain unclaimed after the theatrical run, the funds will be used to support future Angel Studios projects and Pay it Forward initiatives, continuing our mission to amplify light.',
      },
    ],
  },
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  projectMeta,
  newDavidPifLayout,
  pifNameInCreditsEnabled,
}) => {
  const { t } = useTranslate('pif')

  const projectName = projectMeta.name
  const optionalDescription = t(
    'payItForwardForProjectDescriptionv2',
    'Pay it Forward for {{projectName}} on Angel and help support stories you love, enabling others to experience them too',
    {
      projectName,
    },
  )

  return (
    <div className="bg-core-gray-100">
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath:
            projectMeta.slug === slugs.david
              ? 'v1761851105/david/open-graph-david-251030b.jpg'
              : projectMeta.discoveryPosterLandscapeCloudinaryPath,
          cloudinaryTransformation: cloudinaryImageTransformation,
          description:
            projectMeta.slug === slugs.david
              ? t(
                  'pifDavidSeoDescription',
                  'Join the movement to send 1 million kids to see DAVID: The Animated Movie in theaters for free. Pay it Forward and share a story of faith and courage.',
                )
              : projectMeta.pifExplainerText || optionalDescription,
          locale,
          path: `${paths.payItForward.index}/${projectMeta.slug}`,
          title:
            projectMeta.slug === slugs.david
              ? t('pifDavidSeoTitle', 'Help 1 Million Kids See DAVID in Theaters This Christmas')
              : t('payItForwardForProjectAngel', 'Pay it Forward for {{projectName}} on Angel', { projectName }),
        })}
      />
      <ProjectMetaProvider project={projectMeta}>
        <MarketingVideoPopup />
        {slugs.david === projectMeta.slug ? (
          <PayItForwardOfferDataProvider offers={offers}>
            <TheatricalPayItForwardView
              newPifLayout={newDavidPifLayout}
              displayNumberOfTickets={false}
              theatricalRelease={davidTheatricalRelease}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
          </PayItForwardOfferDataProvider>
        ) : (
          <PayItForwardExpressCheckoutView project={projectMeta} offers={offers} />
        )}
      </ProjectMetaProvider>
    </div>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, params, preview, req, res }) => {
    const slug = params?.projectSlug as string

    const projectMeta = await getProjectMeta({ slug }, federationClient, { locale, preview })
    const offers = await getOffersForProject({ projectSlug: slug }, federationClient)

    if (!offers?.length || !projectMeta || !projectMeta.pifEnabled) {
      return {
        redirect: {
          destination: paths.payItForward.forYourConsideration,
          permanent: false,
        },
      }
    }

    // TODO Remove once Truth and Treason Movie is out of theaters
    if (slug === slugs.truthAndTreasonSeries) {
      return {
        redirect: {
          destination: paths.payItForward.truthAndTreason,
          permanent: false,
        },
      }
    }

    // TODO Remove once SOF is out of theaters
    const cloudinaryImageTransformation = buildBrandedCloudinaryImageTransforms({
      logoCloudinaryPath: projectMeta.logoCloudinaryPath,
    })

    const experiments =
      slug === slugs.david
        ? await runExperimentsOnServer(req, res, [
            { experimentKey: 'david-pif-old-vs-new-v2', defaultValue: false },
            { experimentKey: 'pif-name-in-credits', defaultValue: false },
          ])
        : null

    const newDavidPifLayout = experiments?.['david-pif-old-vs-new-v2'] ?? false
    const pifNameInCreditsEnabled = experiments?.['pif-name-in-credits'] ?? false

    return {
      props: {
        cloudinaryImageTransformation,
        locale,
        offers,
        projectMeta,
        newDavidPifLayout,
        pifNameInCreditsEnabled,
        ...(await loadTranslations(locale, ['common', 'pif', 'account'])),
      },
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export default PayItForwardProjectCheckoutPage
